import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { makeStyles, Tooltip } from '@material-ui/core'
import ProgressBar from '../../../../../atoms/ProgressBar'
import { scoreColor } from '../shared/scoreColor'

const useStyles = makeStyles(() => ({
  card: {
    border: '1px solid var(--color-neutral-200)',
    borderRadius: 'var(--border-radius-sm)',
    backgroundColor: 'white',
    padding: '10px 12px',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px'
  },
  head: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: 'var(--gap-sm)'
  },
  name: {
    fontFamily: 'var(--font-mono, monospace)',
    fontSize: 'var(--text-size-sm)',
    color: 'var(--color-neutral-800)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  value: {
    fontSize: 'var(--text-size-md, 16px)',
    fontWeight: 700,
    color: 'var(--color-neutral-900)',
    fontVariantNumeric: 'tabular-nums'
  },
  meta: {
    fontSize: '10px',
    color: 'var(--color-neutral-400)',
    display: 'flex',
    gap: '8px'
  },
  kindBadge: {
    fontSize: '9px',
    fontWeight: 700,
    fontFamily: 'var(--font-mono, monospace)',
    color: 'var(--color-neutral-400)',
    textTransform: 'uppercase',
    letterSpacing: '0.4px'
  },
  dots: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '3px',
    paddingTop: '2px'
  },
  dot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    border: '1px solid rgba(0,0,0,0.05)',
    flexShrink: 0
  }
}))

const RATING_MIN = 1
const RATING_MAX = 5

function summarize (entry) {
  if (entry.kind === 'boolean') {
    const percentage = (entry.trueRate || 0) * 100
    return {
      percentage,
      displayValue: `${percentage.toFixed(0)}%`,
      kindLabel: 'pass/fail',
      meta: `${entry.trueCount}/${entry.n} true`
    }
  }
  const normalized = Math.max(0, Math.min(1, (entry.mean - RATING_MIN) / (RATING_MAX - RATING_MIN)))
  return {
    percentage: normalized * 100,
    displayValue: `${entry.mean.toFixed(2)} / ${RATING_MAX}`,
    kindLabel: 'rating',
    meta: `\u00B1 ${entry.stddev.toFixed(2)} \u00B7 n=${entry.n}`
  }
}

function MetricDots ({ name, kind, samples, classes }) {
  const dots = useMemo(() => {
    return samples
      .filter(s => s.judgement && s.judgement[name] !== undefined && s.judgement[name] !== null)
      .map(s => {
        const v = s.judgement[name]
        if (kind === 'boolean') {
          return {
            sampleIndex: s.sampleIndex,
            color: v ? 'var(--color-success-500, #22c55e)' : 'var(--color-danger-500, #ef4444)',
            tooltip: `#${s.sampleIndex}: ${v ? 'pass' : 'fail'}`
          }
        }
        const num = Number(v)
        const pct = Math.max(0, Math.min(1, (num - RATING_MIN) / (RATING_MAX - RATING_MIN))) * 100
        return {
          sampleIndex: s.sampleIndex,
          color: scoreColor(pct),
          tooltip: `#${s.sampleIndex}: ${num.toFixed(1)} / ${RATING_MAX}`
        }
      })
  }, [name, kind, samples])

  if (!dots.length) return null
  return (
    <div className={classes.dots}>
      {dots.map((d, i) => (
        <Tooltip key={i} title={d.tooltip} arrow>
          <span className={classes.dot} style={{ backgroundColor: d.color }} />
        </Tooltip>
      ))}
    </div>
  )
}

MetricDots.propTypes = {
  name: PropTypes.string.isRequired,
  kind: PropTypes.string.isRequired,
  samples: PropTypes.array.isRequired,
  classes: PropTypes.object.isRequired
}

function MetricCard ({ name, entry, samples }) {
  const classes = useStyles()
  const { percentage, displayValue, kindLabel, meta } = summarize(entry)

  return (
    <div className={classes.card}>
      <div className={classes.head}>
        <span className={classes.name}>{name}</span>
        <span className={classes.value}>{displayValue}</span>
      </div>
      <ProgressBar
        percentage={percentage}
        color={scoreColor(percentage)}
        backgroundColor='var(--color-neutral-100)'
      />
      <div className={classes.meta}>
        <span className={classes.kindBadge}>{kindLabel}</span>
        <span>{meta}</span>
      </div>
      <MetricDots name={name} kind={entry.kind} samples={samples} classes={classes} />
    </div>
  )
}

MetricCard.propTypes = {
  name: PropTypes.string.isRequired,
  entry: PropTypes.shape({
    kind: PropTypes.string.isRequired,
    n: PropTypes.number,
    mean: PropTypes.number,
    stddev: PropTypes.number,
    trueRate: PropTypes.number,
    trueCount: PropTypes.number
  }).isRequired,
  samples: PropTypes.array.isRequired
}

export default MetricCard
